<template>
	<div class="fixed z-[15] overflow-hidden">
		<div v-if="tooltip && !sidebarOpen" ref="floatingTooltips" class="pointer-events-none">
			<div
				class="fixed z-50 ml-[72px] mt-0.5 whitespace-nowrap rounded-md bg-gray-100 px-2 py-1 text-sm font-semibold text-gray-900 shadow-lg dark:bg-gray-900 dark:text-white"
				:style="{ top: tooltipPosition + (tooltip.isBalance ? 0 : 10) + 'px' }"
			>
				{{ tooltip.title }}
				<div
					class="absolute -left-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 bg-gray-100 dark:bg-gray-900"
				></div>
			</div>
		</div>
		<div
			class="sideNav left-0 top-0 z-10 hidden h-full w-20 flex-col justify-between overflow-hidden bg-secondary pt-[66px] text-white transition-all sm:flex"
			:class="{ 'w-[220px]': sidebarOpen }"
		>
			<div class="no-scrollbar relative h-full">
				<div v-if="shouldShowBalance" class="mb-4 mt-6 h-[52px] overflow-hidden">
					<transition name="fade" mode="out-in">
						<div v-if="sidebarOpen" key="full" class="pl-[26px]">
							<div class="text-sm text-gray-400">Balance</div>
							<div class="text-2xl font-light tracking-wide">{{ $global.currencyGbp(balance) }}</div>
						</div>
						<div v-else key="icon" class="flex h-full items-center justify-center">
							<span
								class="fa-light fa-wallet text-2xl opacity-70"
								@mouseenter="
									showTooltip($event, { title: $global.currencyGbp(balance), isBalance: true }, -1)
								"
								@mouseleave="tooltip = null"
							></span>
						</div>
					</transition>
				</div>
				<ul
					ref="menuList"
					style="scrollbar-width: none"
					class="no-scrollbar h-full items-start overflow-y-auto px-0 pb-10"
					:class="{
						'pt-2': !shouldShowBalance
					}"
				>
					<li
						v-for="(item, index) in filteredMenuItems"
						:key="item.role"
						class="flex items-center justify-center px-0 transition-all"
					>
						<router-link
							:class="{ selected: isSubPage(item.page) }"
							active-class="selected transition-all"
							class="group relative flex flex-nowrap items-center gap-3 pl-[26px] pr-0 transition-all"
							:to="item.to"
							@mouseenter="showTooltip($event, item, index)"
							@mouseleave="tooltip = null"
						>
							<div class="flex min-w-[28px] items-center justify-center">
								<span
									:title="item.title"
									class="mx-auto h-auto"
									:class="[
										item.iconClass,
										{ 'fa-solid': isSubPage(item.page) },
										{ 'fa-light': !isSubPage(item.page) }
									]"
								></span>
							</div>
							<transition-fade>
								<span v-if="sidebarOpen" class="whitespace-nowrap">
									{{ item.title }}
								</span>
							</transition-fade>
							<transition-fade>
								<span
									v-if="item.new && sidebarOpen"
									class="mt-0.5 rounded-full bg-red-500 px-2 py-1 text-xs font-semibold text-white"
									>NEW</span
								>
							</transition-fade>
							<transition-fade>
								<span
									v-if="item.beta && sidebarOpen"
									class="mt-0.5 rounded-full bg-blue-500 px-2 py-1 text-xs font-semibold text-white"
									>BETA</span
								>
							</transition-fade>
						</router-link>
					</li>
				</ul>
				<div
					class="pointer-events-none absolute left-0 top-0 h-12 w-full bg-gradient-to-b from-secondary to-transparent transition-opacity duration-300"
					:class="{ 'opacity-100': isScrolled, 'opacity-0': !isScrolled }"
				></div>
				<div
					class="pointer-events-none absolute bottom-8 left-0 h-12 w-full bg-gradient-to-t from-secondary to-transparent transition-opacity duration-300"
					:class="{ 'opacity-100': !isScrolledToBottom && menuList, 'opacity-0': isScrolledToBottom }"
				></div>
			</div>
			<div class="absolute bottom-0 flex w-full items-center justify-center bg-secondary px-4 pb-4 pt-0">
				<p class="text-xs opacity-50">v{{ version }}</p>
			</div>
		</div>
	</div>
</template>

<script>
import packageJson from '../../package.json'
import { useElementBounding } from '@vueuse/core'
import { getFilteredNavigation } from '../config/navigation'

export default {
	inject: ['user', 'userRoles', 'showContactFormModal', 'tenants', 'sidebarOpen', 'userAccounts'],

	data() {
		return {
			version: packageJson.version,
			tooltip: null,
			tooltipPosition: 0,
			isScrolled: false,
			isScrolledToBottom: false,
			menuList: null
		}
	},

	computed: {
		filteredMenuItems() {
			return getFilteredNavigation(this.userRoles, this.$global).map((item) => ({
				...item,
				to: item.path,
				page: item.path.substring(1)
			}))
		},
		shouldShowBalance() {
			return (
				this.userRoles.includes('account') &&
				this.userAccounts?.find((account) => account.billingType === 'PRE_PAID')
			)
		},
		balance() {
			if (!this.shouldShowBalance) return 0
			return this.userAccounts?.reduce((acc, account) => acc + parseFloat(account.balanceGbp), 0) || 0
		}
	},

	mounted() {
		this.menuList = this.$refs.menuList
		if (this.menuList) {
			this.menuList.addEventListener('scroll', this.handleScroll)
			this.checkScrollPosition()
		}
	},

	beforeUnmount() {
		if (this.menuList) {
			this.menuList.removeEventListener('scroll', this.handleScroll)
		}
	},
	methods: {
		isSubPage(page) {
			return this.$route.path.includes(`/${page}`)
		},
		toggleSidebar() {
			this.sidebarOpen = !this.sidebarOpen
			localStorage.setItem('sidebarOpen', this.sidebarOpen ? 'true' : 'false')
		},
		showTooltip(event, item, index) {
			const target = event.currentTarget
			const { top } = useElementBounding(target)
			this.tooltip = item
			this.tooltipPosition = top
		},
		handleScroll(event) {
			this.isScrolled = event.target.scrollTop > 20
			this.checkScrollPosition()
		},
		checkScrollPosition() {
			if (!this.menuList) return

			const { scrollTop, scrollHeight, clientHeight } = this.menuList
			this.isScrolledToBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 5
		}
	}
}
</script>

<style scoped>
.sideNav {
	-ms-overflow-style: none;
	scrollbar-width: none;
}

.sideNav::-webkit-scrollbar {
	display: none;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.calculated-mh {
	max-height: calc(100vh - 120px) !important;
}

@supports (height: 100dvh) {
	.calculated-mh {
		max-height: calc(100dvh - 120px) !important;
	}
}
</style>
